import { useCallback, useState } from 'react';

import { ContentNugget, transformSearchResultsToNuggets } from '@backrs_ui/common-ui';

import { SearchCategory } from '@backrs_ui/grow-ui/src/pages/Search/search.types';
import { ENVIRONMENT_URL } from '@backrs_ui/grow-ui/src/settings';
import { fetchWrapper as fetch } from '@backrs_ui/grow-ui/src/utils/fetch/fetchWrapper';

import { CAREER_PATH_CONSTANTS } from '../constants';

type LazyLoadState = Readonly<{
  currentPage: number;
  allResults: ContentNugget[];
  hasMore: boolean;
  isLoadingMore: boolean;
}>;

export const useLazyLoadResults = (careerPathName: string, selectedFilters: SearchCategory[]) => {
  const [lazyLoadState, setLazyLoadState] = useState<LazyLoadState>({
    currentPage: CAREER_PATH_CONSTANTS.LAZY_LOAD.INITIAL_PAGE,
    allResults: [],
    hasMore: true,
    isLoadingMore: false,
  });

  const loadMoreResults = useCallback(async (): Promise<void> => {
    if (lazyLoadState.isLoadingMore || !lazyLoadState.hasMore) return;

    setLazyLoadState((prev) => ({ ...prev, isLoadingMore: true }));

    try {
      const nextPage = lazyLoadState.currentPage + 1;
      const url = new URL(`${ENVIRONMENT_URL}/content-search`);
      const filter = JSON.stringify({
        q: careerPathName,
        ...((selectedFilters?.length || 0) > 0 && { types: selectedFilters }),
      });
      const sort = JSON.stringify([
        CAREER_PATH_CONSTANTS.SEARCH.SORT_FIELD,
        CAREER_PATH_CONSTANTS.SEARCH.SORT_DIRECTION,
      ]);

      url.searchParams.append('filter', filter);
      url.searchParams.append('sort', sort);
      url.searchParams.append('page', nextPage.toString());
      url.searchParams.append(
        'perPage',
        CAREER_PATH_CONSTANTS.LAZY_LOAD.RESULTS_PER_PAGE.toString(),
      );

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      const newNuggets = transformSearchResultsToNuggets(data.results).nuggets;

      setLazyLoadState((prev) => ({
        ...prev,
        allResults: [...prev.allResults, ...newNuggets],
        currentPage: nextPage,
        hasMore: newNuggets.length === CAREER_PATH_CONSTANTS.LAZY_LOAD.RESULTS_PER_PAGE,
        isLoadingMore: false,
      }));
    } catch (error) {
      console.error('Failed to load more results:', error);
      setLazyLoadState((prev) => ({ ...prev, isLoadingMore: false }));
    }
  }, [
    careerPathName,
    selectedFilters,
    lazyLoadState.currentPage,
    lazyLoadState.isLoadingMore,
    lazyLoadState.hasMore,
  ]);

  const resetLazyLoadState = useCallback(() => {
    setLazyLoadState({
      currentPage: CAREER_PATH_CONSTANTS.LAZY_LOAD.INITIAL_PAGE,
      allResults: [],
      hasMore: true,
      isLoadingMore: false,
    });
  }, []);

  return {
    lazyLoadState,
    loadMoreResults,
    resetLazyLoadState,
  };
};
